/**
 * useFortifyEvents — detects newly-arrived `fortify` events in GameState
 * and returns animation entries for the fortify trail layer.
 *
 * Contract:
 *   - The FIRST observed `events` array is treated as the "already seen"
 *     baseline so historical fortifies don't replay on mount.
 *   - Each `fortify` event produces ONE entry: source territory,
 *     destination territory, army count, and the player's slot colour.
 *   - A Set of already-animated ts values prevents re-fires on StrictMode
 *     double-invoke, reconnects, or cache replays.
 */

import React from 'react'
import type { GameEvent } from '@warcarp/api-types'
import { useDeferredAnimation } from './useDeferredAnimation'

// The post-conquest "free fortify" carries the same from/to/armies shape as a
// regular fortify, so it draws the same trail line on the map.
type FortifyEvent = Extract<GameEvent, { kind: 'fortify' | 'post-conquest-transfer' }>

export interface FortifyAnimationEntry {
  fromTerritoryId: string
  toTerritoryId: string
  armies: number
  /** Slot colour hex for the moving player (from PLAYER_COLORS). */
  playerColor: string
  /** epoch-ms when the animation was queued — used to clean up stale entries */
  startedAt: number
  /** Source event time distinguishes movements queued in the same batch. */
  eventTs?: number
}

export function fortifyTrailKey(entry: FortifyAnimationEntry) {
  return `fortify-${entry.fromTerritoryId}-${entry.toTerritoryId}-${entry.eventTs ?? entry.startedAt}`
}

/**
 * Pure diff function — exported for unit tests.
 *
 * Returns all `fortify` and `post-conquest-transfer` (free-fortify) events whose
 * `ts` is strictly greater than `lastSeenTs` AND that actually moved troops
 * (`armies > 0`) — both kinds draw the same trail. A zero-troop move (declining
 * a free fortify, or a no-op normal fortify) is not a real movement, so it
 * draws no arrow.
 */
export function detectNewFortifies(
  nextEvents: GameEvent[] | undefined,
  lastSeenTs: number,
): FortifyEvent[] {
  if (!nextEvents || nextEvents.length === 0) return []
  return nextEvents.filter(
    (e): e is FortifyEvent =>
      (e.kind === 'fortify' || e.kind === 'post-conquest-transfer') &&
      e.ts > lastSeenTs &&
      e.armies > 0 &&
      // Fog of war: an end the viewer couldn't see arrives as '' — the log
      // shows the move, but there is nothing on the board to animate.
      e.fromTerritoryId !== '' && e.toTerritoryId !== '',
  )
}

/** Duration the animation runs (ms). After this + buffer, the entry is removed.
 * Trail: 400ms draw + 250ms hold + 300ms fade = 950ms; reduced motion = 800ms.
 * Keep the existing sweep buffer so it cannot cut either path short. */
const ANIM_DURATION_MS = 1300

export function useFortifyEvents(
  events: GameEvent[] | undefined,
  slotByPlayerId: Record<string, number>,
  playerColorHex: (slot: number) => string,
  /**
   * Retained for caller compatibility only — no longer used. The mount
   * baseline is always the max ts present at mount (see the effect below).
   * Keying off the WS-session start made phase remounts flush the whole
   * session's fortifies on the next state change; anchoring to maxTs fixes it.
   */
  viewerJoinedAt?: number,
): FortifyAnimationEntry[] {
  const lastSeenTsRef = React.useRef<number | null>(null)
  const animatedTsSet = React.useRef<Set<number>>(new Set())

  const [entries, setEntries] = React.useState<FortifyAnimationEntry[]>([])
  const defer = useDeferredAnimation()

  React.useEffect(() => {
    if (lastSeenTsRef.current === null) {
      // Baseline = highest ts present at mount; everything present now counts
      // as already-seen so a phase remount never replays the session's prior
      // fortifies. Only events arriving after mount animate. (Was
      // viewerJoinedAt-1, which flushed history on the next state change.)
      const baseline =
        events && events.length > 0
          ? Math.max(...events.map((e) => e.ts))
          : 0
      lastSeenTsRef.current = baseline
      // Pre-populate animatedTsSet with everything at/below the baseline so
      // the dedup guard doesn't fire if the same ts arrives again via WS push.
      if (events) {
        for (const e of events) {
          if (e.ts <= baseline) animatedTsSet.current.add(e.ts)
        }
      }
      return
    }

    const newFortifies = detectNewFortifies(events, lastSeenTsRef.current)
    if (newFortifies.length === 0) return

    const maxNewTs = Math.max(...newFortifies.map((e) => e.ts))
    lastSeenTsRef.current = Math.max(lastSeenTsRef.current, maxNewTs)

    const fresh = newFortifies.filter((e) => !animatedTsSet.current.has(e.ts))
    if (fresh.length === 0) return

    for (const e of fresh) animatedTsSet.current.add(e.ts)

    const now = Date.now()
    const newEntries: FortifyAnimationEntry[] = fresh.map((e) => {
      const slot = slotByPlayerId[e.player] ?? 1
      return {
        fromTerritoryId: e.fromTerritoryId,
        toTerritoryId: e.toTerritoryId,
        armies: e.armies,
        playerColor: playerColorHex(slot),
        startedAt: now,
        eventTs: e.ts,
      }
    })

    // Deferred so the setState re-render does not compete with a tap gesture
    // still resolving on this frame — that is what gives the animation a clean
    // first frame on iOS. Scheduled through useDeferredAnimation, which cancels
    // only on UNMOUNT: cancelling in this effect's own cleanup is what silently
    // ate the fortify trail, because the effect is keyed on `events` and the
    // callers pass `gameState.events` — a new array identity on every state
    // patch, so the next patch abandoned the dispatch before it could run.
    defer.afterInteractions(() => {
      setEntries((prev) => [...prev, ...newEntries])
    })

    defer.later(() => {
      const cutoff = now
      setEntries((prev) => prev.filter((e) => e.startedAt > cutoff))
    }, ANIM_DURATION_MS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events])

  return entries
}
